import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography
} from '@mui/material';

import {
    Block,
    Header,
    List,
    Popup
} from '../CommonComponents';
import { AutocompleteField } from '../CommonComponents/Form';
import { Container } from '../StyledComponents';
import FetchRedux from '../CommonComponents/Fetch/FetchRedux';
import { FetchPropsType } from '../CommonComponents/Fetch/FetchPropsType';
import {
    fetchOrderRegistrationById,
    selectFetchingOrderRegistrationById,
    selectOrderRegistrationById
} from '../../redux/actions/orderRegistrations';
import {
    acceptOrderRegistration,
    createOrderRegistrationAccount,
    linkOrderRegistrationAccount,
    reactivateOrderRegistration,
    rejectOrderRegistration
} from '../../api-admin/order-registrations';
import { searchTenants } from '../../api-admin/tenants';
import {
    OrderRegistrationRecipientType,
    OrderRegistrationStatusType,
    OrderRegistrationType
} from '../../types';
import toast from '../../utils/toast';
import {
    formatDate,
    formatDateTime,
    formatIpAddress,
    formatNumber,
    formatPhone
} from '../../utils/parsing';
import { RowTypeType } from '../CommonComponents/List';

const statusLabel: Record<OrderRegistrationStatusType, string> = {
    pending: 'Väntande',
    processing: 'Behandlas',
    accepted: 'Accepterad',
    rejected: 'Nekad'
};

type TenantSearchOption = {
    id: number,
    name: string,
    orgNumber?: string,
    isRegistered?: boolean,
    isCarrier?: boolean
};

type AccountMode = 'createNew' | 'linkExisting';

function normalizeOrg(value?: string | null) {
    return (value || '').replace(/[-\s]/g, '').toLowerCase();
}

function OrderRegistrationDetails(props: FetchPropsType<OrderRegistrationType>) {
    const { entity, fetch } = props;
    const navigate = useNavigate();

    const [busy, setBusy] = React.useState(false);
    const [acceptOpen, setAcceptOpen] = React.useState(false);
    const [rejectOpen, setRejectOpen] = React.useState(false);
    const [accountOpen, setAccountOpen] = React.useState(false);
    const [accountMode, setAccountMode] = React.useState<AccountMode>('createNew');
    const [accountOrgNumber, setAccountOrgNumber] = React.useState('');
    const [accountPhone, setAccountPhone] = React.useState('');
    const [accountMessage, setAccountMessage] = React.useState('');
    const [suggesting, setSuggesting] = React.useState(false);
    const [suggestedTenants, setSuggestedTenants] = React.useState<TenantSearchOption[]>([]);
    const [selectedTenant, setSelectedTenant] = React.useState<TenantSearchOption | null>(null);
    const [rejectMessage, setRejectMessage] = React.useState('');
    const [sendRejectEmail, setSendRejectEmail] = React.useState(true);
    const [carrierWarning, setCarrierWarning] = React.useState(false);

    const hasAccount = !!(entity.buyerTenantId && entity.buyerUserId);
    const isPending = entity.status === 'pending';
    const isProcessing = entity.status === 'processing';
    const isRejected = entity.status === 'rejected';
    const isAccepted = entity.status === 'accepted';

    const recipients = React.useMemo(() => {
        if (Array.isArray(entity.recipients)) {
            return entity.recipients as OrderRegistrationRecipientType[];
        }
        return [];
    }, [entity.recipients]);

    const fetchTenantOptions = React.useCallback(async (query?: string) => {
        if (!query || query.trim().length < 2) {
            return [];
        }
        const results = await searchTenants(query.trim());
        return (results || []).slice(0, 10) as TenantSearchOption[];
    }, []);

    const run = React.useCallback(async (
        action: () => Promise<any>,
        errorMessage: string,
        onSuccess?: (result: any) => void
    ) => {
        setBusy(true);
        try {
            const result = await action();
            if (onSuccess) {
                onSuccess(result);
            } else {
                await fetch();
            }
        } catch (e) {
            toast(e, errorMessage);
        } finally {
            setBusy(false);
        }
    }, [fetch]);

    const applySuggestion = React.useCallback((tenants: TenantSearchOption[]) => {
        setSuggestedTenants(tenants);
        const orgNorm = normalizeOrg(entity.companyOrgNumber);
        const exactOrg = orgNorm
            ? tenants.find((t) => normalizeOrg(t.orgNumber) === orgNorm)
            : undefined;
        if (exactOrg) {
            setAccountMode('linkExisting');
            setSelectedTenant(exactOrg);
            setAccountMessage(`Förslag: koppla till befintligt företag ${exactOrg.name}.`);
            return;
        }
        if (tenants.length === 1) {
            setAccountMode('linkExisting');
            setSelectedTenant(tenants[0]);
            setAccountMessage(`Förslag: koppla till befintligt företag ${tenants[0].name}.`);
            return;
        }
        if (tenants.length > 1) {
            setAccountMode('linkExisting');
            setSelectedTenant(tenants[0]);
            setAccountMessage(
                `Förslag: ${tenants.length} företag matchade. Välj vilket som ska kopplas, eller skapa nytt.`
            );
            return;
        }
        setAccountMode('createNew');
        setSelectedTenant(null);
        setAccountMessage(
            'Förslag: skapa nytt företag och användare utifrån förfrågans uppgifter.'
        );
    }, [entity.companyOrgNumber]);

    const openAccountModal = React.useCallback(async () => {
        setAccountOrgNumber(entity.companyOrgNumber || '');
        setAccountPhone(entity.phone || '');
        setSelectedTenant(null);
        setSuggestedTenants([]);
        setAccountMessage('');
        setAccountMode('createNew');
        setAccountOpen(true);
        setSuggesting(true);
        try {
            const queries = [
                entity.companyOrgNumber,
                entity.companyName
            ].filter((q): q is string => !!q && q.trim().length >= 2);

            const uniqueQueries = Array.from(new Set(queries.map((q) => q.trim())));
            const batches = await Promise.all(
                uniqueQueries.map((q) => searchTenants(q))
            );
            const byId = new Map<number, TenantSearchOption>();
            batches.flat().forEach((t: TenantSearchOption) => {
                if (t?.id) {
                    byId.set(t.id, t);
                }
            });
            applySuggestion(Array.from(byId.values()));
        } catch (e) {
            setAccountMode('createNew');
            setAccountMessage(
                'Kunde inte söka efter företag. Du kan skapa nytt eller söka manuellt.'
            );
            toast(e, 'Kunde inte söka efter företag');
        } finally {
            setSuggesting(false);
        }
    }, [
        applySuggestion,
        entity.companyName,
        entity.companyOrgNumber,
        entity.phone
    ]);

    const onSaveAccount = React.useCallback(() => {
        if (accountMode === 'linkExisting') {
            if (!selectedTenant?.id) {
                toast('Välj ett företag att koppla till');
                return;
            }
            run(
                () => linkOrderRegistrationAccount(entity.id, { tenantId: selectedTenant.id }),
                'Kunde inte koppla konto',
                async (result) => {
                    if (result?.existingAccount && result.candidate) {
                        const candidate = result.candidate as TenantSearchOption;
                        setSelectedTenant(candidate);
                        setSuggestedTenants((prev) => {
                            const byId = new Map(prev.map((t) => [t.id, t]));
                            byId.set(candidate.id, candidate);
                            return Array.from(byId.values());
                        });
                        setAccountMessage(
                            result.message
                            || `E-postadressen tillhör redan ${candidate.name}. Välj det företaget.`
                        );
                        return;
                    }
                    setAccountOpen(false);
                    if (result?.candidateIsCarrier || selectedTenant.isCarrier) {
                        setCarrierWarning(true);
                    }
                    toast('Konto kopplat');
                    await fetch();
                }
            );
            return;
        }

        if (!accountOrgNumber.trim()) {
            toast('Ange organisationsnummer');
            return;
        }

        run(
            () => createOrderRegistrationAccount(entity.id, {
                companyOrgNumber: accountOrgNumber.trim(),
                phone: accountPhone.trim() || undefined
            }),
            'Kunde inte skapa konto',
            async (result) => {
                if (result?.needsFields) {
                    setAccountMessage(result.message || 'Fyll i saknade uppgifter.');
                    return;
                }
                if (result?.existingAccount && result.candidate) {
                    const candidate = result.candidate as TenantSearchOption & {
                        userId?: number,
                        userName?: string,
                        email?: string
                    };
                    setAccountMode('linkExisting');
                    setSelectedTenant(candidate);
                    setSuggestedTenants([candidate]);
                    setAccountMessage(
                        result.message
                        || `E-postadressen tillhör redan ${candidate.name}. Koppla till det företaget.`
                    );
                    return;
                }
                if (result?.existingOrgNumber && result.candidates?.length) {
                    const candidates = result.candidates as TenantSearchOption[];
                    setAccountMode('linkExisting');
                    setSuggestedTenants(candidates);
                    setSelectedTenant(candidates[0] || null);
                    setAccountMessage(
                        result.message
                        || 'Organisationsnumret finns redan. Välj företag att koppla till.'
                    );
                    return;
                }
                if (result?.needsSelection && result.candidates?.length) {
                    const candidates = result.candidates as TenantSearchOption[];
                    applySuggestion(candidates);
                    setAccountMessage(
                        'Flera företag matchade. Välj vilket som ska kopplas, eller behåll skapa nytt.'
                    );
                    return;
                }
                setAccountOpen(false);
                if (result?.candidateIsCarrier) {
                    setCarrierWarning(true);
                }
                toast('Konto skapat');
                await fetch();
            }
        );
    }, [
        accountMode,
        accountOrgNumber,
        accountPhone,
        applySuggestion,
        entity.id,
        fetch,
        run,
        selectedTenant
    ]);

    const onAccept = React.useCallback(() => {
        run(
            () => acceptOrderRegistration(entity.id),
            'Kunde inte acceptera förfrågan',
            async (result) => {
                setAcceptOpen(false);
                toast('Förfrågan accepterad och uppdrag publicerat');
                if (result?.orderId) {
                    navigate(`/orders/${result.orderId}`);
                } else {
                    await fetch();
                }
            }
        );
    }, [entity.id, fetch, navigate, run]);

    const onReject = React.useCallback(() => {
        const message = sendRejectEmail ? rejectMessage.trim() : undefined;
        if (sendRejectEmail && !message) {
            toast('Skriv ett meddelande eller avmarkera e-post');
            return;
        }
        run(
            () => rejectOrderRegistration(entity.id, message),
            'Kunde inte neka förfrågan',
            async () => {
                setRejectOpen(false);
                setRejectMessage('');
                toast('Förfrågan nekad');
                await fetch();
            }
        );
    }, [entity.id, fetch, rejectMessage, run, sendRejectEmail]);

    const onReactivate = React.useCallback(() => {
        run(
            () => reactivateOrderRegistration(entity.id),
            'Kunde inte återaktivera förfrågan',
            async (result) => {
                if (result?.finalized && result?.orderId) {
                    toast('Acceptans slutförd');
                    navigate(`/orders/${result.orderId}`);
                    return;
                }
                toast('Förfrågan återaktiverad');
                await fetch();
            }
        );
    }, [entity.id, fetch, navigate, run]);

    const proposalTenant = selectedTenant
        || (suggestedTenants.length === 1 ? suggestedTenants[0] : null);

    const contactRows = [{
        name: 'Namn',
        value: entity.name || '-'
    }, {
        name: 'E-post',
        value: entity.email || '-'
    }, {
        name: 'Telefon',
        value: formatPhone(entity.phone) || '-'
    }, {
        name: 'Företag',
        value: entity.companyName || '-'
    }, {
        name: 'Org.nr',
        value: entity.companyOrgNumber || '-'
    }];

    const shipmentRows = [{
        name: 'Upphämtning',
        value: [
            entity.pickupStreet,
            [entity.pickupZip, entity.pickupCity].filter(Boolean).join(' '),
            entity.pickupCountry
        ].filter(Boolean).join(', ') || '-'
    }, {
        name: 'Leverans',
        value: [
            entity.deliveryStreet,
            [entity.deliveryZip, entity.deliveryCity].filter(Boolean).join(' '),
            entity.deliveryCountry
        ].filter(Boolean).join(', ') || '-'
    }, {
        name: 'Upphämtningsdatum',
        value: formatDate(entity.pickupDate) || '-'
    }, {
        name: 'Senaste upphämtning',
        value: formatDate(entity.lastPickupDate) || '-'
    }, {
        name: 'Leveransdatum',
        value: formatDate(entity.deliveryDate) || '-'
    }, {
        name: 'Senaste leverans',
        value: formatDate(entity.lastDeliveryDate) || '-'
    }, {
        name: 'Anmäl intresse senast',
        value: formatDateTime(entity.interestDeadline) || '-'
    }, {
        name: 'Vikt',
        value: formatNumber(entity.grossWeight) || '-',
        type: 'kg' as RowTypeType
    }, {
        name: 'Volym',
        value: formatNumber(entity.volume) || '-'
    }, {
        name: 'Flakmeter',
        value: formatNumber(entity.loadingMeters) || '-'
    }, {
        name: 'Pallar',
        value: formatNumber(entity.pallets) || '-'
    }, {
        name: 'Kolli',
        value: formatNumber(entity.pieces) || '-'
    }, {
        name: 'Gods typ',
        value: entity.shipmentType || '-'
    }, {
        name: 'Riktpris',
        value: entity.listPrice ?? '-',
        type: 'currency' as RowTypeType
    }, {
        name: 'Beskrivning',
        value: entity.description || '-'
    }];

    const metaRows = [{
        name: 'Fordonstyper',
        value: (entity.vehicleTypes && entity.vehicleTypes.length)
            ? entity.vehicleTypes
            : ['-'],
        type: 'array' as RowTypeType
    }, {
        name: 'Utrustning',
        value: (entity.equipment && entity.equipment.length)
            ? entity.equipment
            : ['-'],
        type: 'array' as RowTypeType
    }, {
        name: 'Hållbarhet',
        value: (entity.sustainability && entity.sustainability.length)
            ? entity.sustainability
            : ['-'],
        type: 'array' as RowTypeType
    }, {
        name: 'Licenser/certifikat',
        value: (entity.licencesCertificates && entity.licencesCertificates.length)
            ? entity.licencesCertificates
            : ['-'],
        type: 'array' as RowTypeType
    }];

    const statusRows = [{
        name: 'Status',
        value: statusLabel[entity.status] || entity.status
    }, {
        name: 'Skickad',
        value: formatDateTime(entity.createdAt) || '-'
    }, {
        name: 'IP',
        value: formatIpAddress(entity.ipAddress) || '-'
    }, {
        name: 'Kopplat företag',
        value: entity.buyerTenantName || '-',
        url: entity.buyerTenantId ? `/tenants/${entity.buyerTenantId}` : undefined
    }, {
        name: 'Kopplad användare',
        value: entity.buyerUserName || entity.buyerUserEmail || '-',
        url: entity.buyerUserId ? `/users/${entity.buyerUserId}` : undefined
    }, {
        name: 'Skapat uppdrag',
        value: entity.orderId ? `#${entity.orderId}` : '-',
        url: entity.orderId ? `/orders/${entity.orderId}` : undefined
    }, {
        name: 'Nekandemeddelande',
        value: entity.rejectionMessage || '-'
    }];

    const saveDisabled = busy
        || suggesting
        || (accountMode === 'linkExisting' && !selectedTenant?.id)
        || (accountMode === 'createNew' && !accountOrgNumber.trim());

    return (
        <Container>
            <Header
                title={`Förfrågan #${entity.id}`}
                subtitle={entity.companyName || entity.name}
            />

            {carrierWarning && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Det valda företaget är markerat som åkeri. Kontrollera att det är rätt
                    uppdragsgivare innan du accepterar.
                </Alert>
            )}

            {isPending && (
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                    <Button
                        variant="outlined"
                        disabled={busy || hasAccount}
                        onClick={openAccountModal}
                    >
                        Skapa/länka konto
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        disabled={busy || !hasAccount}
                        onClick={() => setAcceptOpen(true)}
                    >
                        Acceptera
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={busy}
                        onClick={() => setRejectOpen(true)}
                    >
                        Neka
                    </Button>
                </Stack>
            )}

            {isRejected && (
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Button
                        variant="contained"
                        disabled={busy}
                        onClick={onReactivate}
                    >
                        Återaktivera
                    </Button>
                </Stack>
            )}

            {isProcessing && (
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
                    <Alert severity="info" sx={{ flex: 1 }}>
                        {entity.orderId
                            ? `Uppdrag #${entity.orderId} skapades men acceptansen slutfördes inte. `
                                + 'Slutför för att markera förfrågan som accepterad (skapar inte nytt uppdrag).'
                            : 'Förfrågan håller på att accepteras eller fastnade innan uppdrag skapades. '
                                + 'Återaktivera om den har fastnat.'}
                    </Alert>
                    <Button
                        variant="contained"
                        disabled={busy}
                        onClick={onReactivate}
                    >
                        {entity.orderId ? 'Slutför acceptans' : 'Återaktivera'}
                    </Button>
                    {entity.orderId && (
                        <Button
                            variant="outlined"
                            onClick={() => navigate(`/orders/${entity.orderId}`)}
                        >
                            Visa uppdrag
                        </Button>
                    )}
                </Stack>
            )}

            {isAccepted && entity.orderId && (
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => navigate(`/orders/${entity.orderId}`)}
                    >
                        Visa uppdrag
                    </Button>
                </Stack>
            )}

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Block title="Kontakt">
                        <List data={contactRows} />
                    </Block>
                    <Box sx={{ mt: 2 }}>
                        <Block title="Status">
                            <List data={statusRows} />
                        </Block>
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Block title="Transport">
                        <List data={shipmentRows} />
                    </Block>
                    <Box sx={{ mt: 2 }}>
                        <Block title="Krav">
                            <List data={metaRows} />
                        </Block>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <Block title="Mottagare">
                            {recipients.length === 0 ? (
                                <Typography variant="body2" sx={{ p: 2 }}>Inga mottagare</Typography>
                            ) : (
                                <List
                                    data={recipients.map((r) => ({
                                        name: r.name,
                                        value: [
                                            r.orgNumber,
                                            r.isCarrier ? 'Åkeri' : 'Köpare'
                                        ].filter(Boolean).join(' · '),
                                        url: `/tenants/${r.id}`
                                    }))}
                                />
                            )}
                        </Block>
                    </Box>
                </Grid>
            </Grid>

            <Popup
                open={acceptOpen}
                title="Acceptera förfrågan"
                body="Skapar och publicerar ett privat uppdrag under det kopplade kontot, samt notifierar mottagaråkerierna."
                okLabel="Acceptera"
                handleOk={onAccept}
                handleClose={() => setAcceptOpen(false)}
                isSubmitting={busy}
                okDisabled={!hasAccount}
            />

            <Popup
                open={rejectOpen}
                title="Neka förfrågan"
                okLabel="Neka"
                handleOk={onReject}
                handleClose={() => setRejectOpen(false)}
                isSubmitting={busy}
                fullWidth
                maxWidth="sm"
            >
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={sendRejectEmail}
                            onChange={(e) => setSendRejectEmail(e.target.checked)}
                        />
                    )}
                    label="Skicka e-post till avsändaren"
                />
                <TextField
                    label="Meddelande"
                    value={rejectMessage}
                    onChange={(e) => setRejectMessage(e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                    disabled={!sendRejectEmail}
                    sx={{ mt: 1 }}
                />
            </Popup>

            <Popup
                open={accountOpen}
                title="Skapa/länk konto"
                body={suggesting
                    ? 'Söker efter matchande företag...'
                    : (accountMessage || 'Välj om du vill skapa nytt eller koppla till befintligt.')}
                okLabel="Spara"
                handleOk={onSaveAccount}
                handleClose={() => setAccountOpen(false)}
                isSubmitting={busy}
                okDisabled={saveDisabled}
                fullWidth
                maxWidth="sm"
            >
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl>
                        <FormLabel>Åtgärd</FormLabel>
                        <RadioGroup
                            value={accountMode}
                            onChange={(e) => setAccountMode(e.target.value as AccountMode)}
                        >
                            <FormControlLabel
                                value="createNew"
                                control={<Radio />}
                                label="Skapa nytt företag och användare"
                            />
                            <FormControlLabel
                                value="linkExisting"
                                control={<Radio />}
                                label={proposalTenant
                                    ? `Koppla till befintligt${proposalTenant.name ? `: ${proposalTenant.name}` : ''}`
                                    : 'Koppla till befintligt företag'}
                            />
                        </RadioGroup>
                    </FormControl>

                    {accountMode === 'createNew' && (
                        <>
                            <TextField
                                label="Organisationsnummer"
                                value={accountOrgNumber}
                                onChange={(e) => setAccountOrgNumber(e.target.value)}
                                placeholder="556XXX-XXXX"
                                required
                                fullWidth
                                helperText="Format XXXXXX-XXXX"
                            />
                            <TextField
                                label="Telefon"
                                value={accountPhone}
                                onChange={(e) => setAccountPhone(e.target.value)}
                                placeholder="+467XXXXXXXX"
                                fullWidth
                                helperText="Valfritt, men rekommenderas"
                            />
                        </>
                    )}

                    {accountMode === 'linkExisting' && (
                        <>
                            {suggestedTenants.length > 1 && (
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                        Föreslagna träffar — klicka för att välja:
                                    </Typography>
                                    <Stack spacing={0.5}>
                                        {suggestedTenants.map((t) => (
                                            <Button
                                                key={t.id}
                                                size="small"
                                                variant={selectedTenant?.id === t.id ? 'contained' : 'outlined'}
                                                onClick={() => setSelectedTenant(t)}
                                                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                            >
                                                {t.name}
                                                {t.orgNumber ? ` (${t.orgNumber})` : ''}
                                                {t.isCarrier ? ' — åkeri' : ''}
                                            </Button>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                            <AutocompleteField<TenantSearchOption>
                                label="Sök annat företag"
                                optionIdKey="id"
                                fetchOptions={fetchTenantOptions}
                                minQueryLength={2}
                                maxOptions={10}
                                getOptionName={(option) => option.name}
                                getOptionDescription={(option) => (
                                    option.orgNumber ? `Org.nr ${option.orgNumber}` : ''
                                )}
                                displayOption={selectedTenant}
                                onChange={(option: TenantSearchOption | null) => setSelectedTenant(option)}
                                filterOptions={(opts) => opts}
                                variant="outlined"
                                size="small"
                            />
                        </>
                    )}

                    <Typography variant="caption" color="text.secondary">
                        Skapa nytt skapar alltid ett nytt företag. Om e-post eller
                        organisationsnummer redan finns får du välja att koppla istället.
                        När du sparar väljs avsändaren och kan inte ändras.
                    </Typography>
                </Box>
            </Popup>
        </Container>
    );
}

export default function OrderRegistrationDetailsPage() {
    const { id } = useParams();
    const registrationId = parseInt(id || '', 10);

    return (
        <FetchRedux
            name="förfrågan"
            fetchEntity={fetchOrderRegistrationById(registrationId)}
            selectEntity={selectOrderRegistrationById(registrationId)}
            selectIsFetching={selectFetchingOrderRegistrationById(registrationId)}
            Component={OrderRegistrationDetails}
        />
    );
}
